import React from 'react';

import cn from 'classnames';
import PropTypes from 'prop-types';
import ReactSelect from 'react-select';

import bem from 'client/services/bem';
import {useLanguage} from 'client/services/hooks';

import ErrorMessage from 'client/common/inputs/error-message';
import RequiredLabel from 'client/common/inputs/required-label';
import WarningMessage from 'client/common/inputs/warning-message';
import {components, groupedComponents} from 'client/common/selects/select-dropdown/components';
import {SelectCommonProps, SelectOption} from 'client/common/selects/types';

import {getDefaultValue, getSimpleValues} from './helpers';

// We can't use cssModules because there are react-select which doesn't support css-modules
import './select-dropdown.scss';

const b = bem('select-dropdown');

/**
 * @type {React.FC<InferProps<typeof SelectDropdown.propTypes>>}
 */
const SelectDropdown = (props) => {
  const {
    label = null,
    options = [],
    value = null,
    onChange = null,
    name = null,
    className = '',
    labelClassName = '',
    selectClassName = '',
    prefixClassName = null,
    disabled = false,
    multi = false,
    multiLine = false,
    clearable = false,
    noResultsText = null,
    placeholder = '',
    size = null,
    type = null,
    plainly = false,
    searchable = false,
    errorMessage = '',
    warningMessage = '',
    onBlur = null,
    Component = null,
    simpleValue = false,
    grouped = false,
    required = false,
    inputHeight = 'default',
    menuPlacement = 'auto',
    labelColor = 'default',
    singleValueColor = 'default',
    ...selectProps
  } = props;

  const lang = useLanguage('COMMON.SELECTS.SELECT_DROPDOWN');

  const selectClassNames = cn(
    b({
      'is-disabled': disabled,
      error: !!errorMessage,
      warning: !!warningMessage,
      plainly: plainly,
      grouped,
      [size]: size,
      [type]: type,
    }),
    className,
  );

  const handlerOnChange = (optionValues) => {
    onChange?.(simpleValue ? getSimpleValues(optionValues) : optionValues);
  };

  const Select = Component || ReactSelect;

  return (
    <div className={selectClassNames}>
      {label && (
        <label htmlFor={name} className={cn(b('label', [labelColor]), labelClassName)}>
          {label}
        </label>
      )}
      <div className={b('select-element')}>
        <Select
          name={name}
          options={options}
          value={getDefaultValue(value, options)}
          isMulti={multi}
          isDisabled={disabled}
          isClearable={clearable}
          isSearchable={searchable}
          className={cn(b('field'), selectClassName)}
          classNamePrefix={prefixClassName ? prefixClassName : b()}
          noOptionsMessage={() => noResultsText || lang.DROPDOWN_NO_DATA}
          placeholder={placeholder}
          menuPlacement={menuPlacement}
          onChange={handlerOnChange}
          blurInputOnSelect={false} // fixed ios focus bug
          onBlur={() => onBlur?.(value)}
          components={grouped ? groupedComponents : components}
          required={false}
          hideSelectedOptions={false}
          {...selectProps}
          styles={{
            menuPortal: (baseStyles) => ({
              ...baseStyles,
              zIndex: undefined,
            }),
            ...selectProps.styles,
          }}
          classNames={{
            ...selectProps.classNames,
            dropdownIndicator: (...args) =>
              cn(b('indicator', [inputHeight]), selectProps.classNames?.indicator?.(...args)),
            input: (...args) => cn(b('input', [inputHeight]), selectProps.classNames?.input?.(...args)),
            menu: (...args) => cn(b('menu'), selectProps.classNames?.menu?.(...args)),
            menuPortal: (...args) => cn(b('menu-portal'), selectProps.classNames?.menuPortal?.(...args)),
            control: (...args) =>
              cn(
                b('control', {[inputHeight]: true, 'auto-height': multi && multiLine}),
                selectProps.classNames?.control?.(...args),
              ),
            valueContainer: (...args) =>
              cn(
                b('value-container', {'multi-wrap': multi && multiLine}),
                selectProps.classNames?.valueContainer?.(...args),
              ),
            menuList: (...args) => cn(b('menu-list'), selectProps.classNames?.menuList?.(...args)),
            singleValue: (...args) =>
              cn(
                b('single-value', {[`color-${singleValueColor}`]: true}),
                selectProps.classNames?.singleValue?.(...args),
              ),
            option: (...args) => cn(b('option', {['is-multi']: multi}), selectProps.classNames?.option?.(...args)),
          }}
        />
        {!errorMessage && required && <RequiredLabel />}
        {errorMessage && <ErrorMessage errorMessage={errorMessage} />}
        {warningMessage && !errorMessage && <WarningMessage warningMessage={warningMessage} />}
      </div>
    </div>
  );
};

const SelectDropdownValueType = PropTypes.oneOfType([
  PropTypes.string,
  PropTypes.number,
  PropTypes.object,
  SelectOption,
  PropTypes.arrayOf(PropTypes.string),
  PropTypes.arrayOf(PropTypes.number),
  PropTypes.arrayOf(PropTypes.object),
  PropTypes.arrayOf(SelectOption),
]);

SelectDropdown.propTypes = {
  ...SelectCommonProps,
  options: PropTypes.arrayOf(SelectOption),
  value: SelectDropdownValueType,
  searchable: PropTypes.bool,
  Component: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  grouped: PropTypes.bool,
  menuHeaderElement: PropTypes.node,
  menuFooterElement: PropTypes.node,
  required: PropTypes.bool,
  defaultValue: SelectDropdownValueType,
  inputHeight: PropTypes.oneOf(['extra-small', 'small', 'default']),
  labelColor: PropTypes.oneOf(['white', 'default']),
  menuPlacement: PropTypes.oneOf(['auto', 'bottom', 'top']),
  singleValueColor: PropTypes.oneOf(['default', 'primary']),
  multiLine: PropTypes.bool, // Wraps values across multiple lines when `multi=true`
};

export default SelectDropdown;
